import { IGame, IRenderer, IInput } from '../games/GameRegistry.js';
import { GridHitDetector } from '../core/GridHitDetector.js';
import { getCanvasCoordinates } from './canvasCoordinates.js';

export type InputCallback = () => void;

/**
 * Base class for game input handlers.
 * Provides common setup: GridHitDetector creation, canvas reference, and event cleanup.
 * Subclasses must call setupEventListeners() in their constructor after super().
 *
 * @template TGame - The specific game type
 * @template TRenderer - The specific renderer type
 */
export abstract class BaseInput<
    TGame extends IGame = IGame,
    TRenderer extends IRenderer = IRenderer
> implements IInput {
    protected readonly game: TGame;
    protected readonly renderer: TRenderer;
    protected readonly detector: GridHitDetector;
    protected readonly canvas: HTMLCanvasElement;
    protected readonly onUpdate: InputCallback;

    constructor(
        game: TGame,
        renderer: TRenderer,
        canvas: HTMLCanvasElement,
        onUpdate: InputCallback
    ) {
        this.game = game;
        this.renderer = renderer;
        this.canvas = canvas;
        this.onUpdate = onUpdate;

        const config = renderer.getConfig();
        this.detector = new GridHitDetector({
            cellSize: config.cellSize,
            padding: config.padding,
            gridSize: game.getGrid().size
        });
        // Subclasses must call this.setupEventListeners() in their constructor
        // because arrow function fields are initialized after base constructor runs
    }

    /**
     * Set up game-specific event listeners.
     * Must be called by subclass constructor after super().
     */
    protected abstract setupEventListeners(): void;

    /**
     * Remove event listeners. Called by destroy().
     */
    protected abstract removeEventListeners(): void;

    /**
     * Get canvas coordinates from a mouse event.
     */
    protected getCoordinates(e: MouseEvent): { x: number; y: number } {
        return getCanvasCoordinates(this.canvas, e);
    }

    destroy(): void {
        this.removeEventListeners();
    }
}
